'use client';

import {signup} from 'iq-auth/api';
import {completeAuth} from 'iq-auth/post_auth_actions';
import {BackButton} from 'iq-auth/components/back_button';
import {FormField, inputClass} from 'iq-auth/components/form_field';
import {GoogleIcon, LinkedInIcon} from 'iq-auth/components/icons';
import {OAuthLink} from 'iq-auth/components/oauth_link';
import {OrSeparator} from 'iq-auth/components/or_separator';
import {useOAuthUrls} from 'iq-auth/hooks';
import {
  trackSignupAccountSubmitted,
  trackSignupCompleted,
  trackSignupFailed,
  trackSignupMethodSelected,
} from 'iq-auth/tracking';
import {ChevronLeft, ChevronRight, Mail} from 'lucide-react';
import {Button} from '@repo/ui/button';
import {useEffect, useState} from 'react';

type View = 'oauth' | 'manual';

export type SignupFormProps = {
  onSuccess?: () => void;
  onBack?: () => void;
  onSwitchToLogin?: () => void;
  cta?: string;
  initialEmail?: string;
  initialFirstName?: string;
  initialLastName?: string;
  next?: string;
};

export function SignupForm({
  onSuccess,
  onBack,
  onSwitchToLogin,
  cta,
  initialEmail = '',
  initialFirstName = '',
  initialLastName = '',
  next,
}: SignupFormProps) {
  const [view, setView] = useState<View>('oauth');
  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {googleUrl, linkedInUrl} = useOAuthUrls('signup', next);

  useEffect(() => {
    if (initialEmail) setEmail(initialEmail);
    if (initialFirstName) setFirstName(initialFirstName);
    if (initialLastName) setLastName(initialLastName);
  }, [initialEmail, initialFirstName, initialLastName]);

  const passwordsMatch = password === repeatPassword;
  const isValid =
    email.trim().length > 0 &&
    password.length >= 6 &&
    passwordsMatch &&
    firstName.trim().length > 0 &&
    lastName.trim().length > 0;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    void trackSignupAccountSubmitted();
    try {
      await signup(email, password, firstName, lastName);
      void trackSignupCompleted('password');
      await completeAuth({onSuccess, next});
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      void trackSignupFailed('password', message);
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className='flex flex-col'>
      <div className='mb-8'>
        {view === 'oauth' && onBack && <BackButton onBack={onBack} />}
        {view === 'manual' && (
          <button
            type='button'
            onClick={() => {
              setView('oauth');
              setError(null);
            }}
            className='flex items-center gap-1 text-xs text-iq-grey-medium hover:text-iq-black transition-colors mb-4'>
            <ChevronLeft className='w-3 h-3' />
            Other sign up options
          </button>
        )}
        <h2 className='text-2xl font-bold text-iq-black mb-2'>Create your free account</h2>
        {cta && <p className='text-sm text-iq-grey-medium'>{cta}</p>}
      </div>

      {view === 'oauth' ? (
        <div className='flex flex-col gap-3'>
          <OAuthLink
            href={googleUrl}
            icon={<GoogleIcon />}
            label='Sign up with Google'
            onClick={() => void trackSignupMethodSelected('oauth-google')}
          />
          <OAuthLink
            href={linkedInUrl}
            icon={<LinkedInIcon />}
            label='Sign up with LinkedIn'
            onClick={() => void trackSignupMethodSelected('oauth-linkedin')}
          />
          <button
            type='button'
            onClick={() => {
              void trackSignupMethodSelected('password');
              setView('manual');
            }}
            className='flex items-center justify-center gap-3 w-full px-4 py-3 border border-iq-grey-light rounded-lg text-sm font-medium text-iq-grey-medium hover:text-iq-black hover:bg-iq-grey-100 transition-colors'>
            <Mail className='w-[17px] h-[17px] -mt-1' />
            Sign up with email
          </button>
          {onSwitchToLogin && (
            <>
              <OrSeparator />
              <Button
                variant={'outline'}
                className='border border-iq-grey-light rounded-lg text-sm font-medium text-iq-grey-medium hover:text-iq-black hover:bg-iq-grey-100 transition-colors'
                onClick={onSwitchToLogin}>
                Log in
              </Button>
            </>
          )}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className='flex flex-col gap-4'>
          <div className='grid grid-cols-2 gap-3'>
            <FormField label='First name'>
              <input
                type='text'
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder='First name'
                autoComplete='given-name'
                required
                className={inputClass}
              />
            </FormField>
            <FormField label='Last name'>
              <input
                type='text'
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder='Last name'
                autoComplete='family-name'
                required
                className={inputClass}
              />
            </FormField>
          </div>
          <FormField label='Email'>
            <input
              type='email'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder='you@example.com'
              autoComplete='email'
              required
              className={inputClass}
            />
          </FormField>
          <FormField label='Password'>
            <input
              type='password'
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder='Minimum 6 characters'
              autoComplete='new-password'
              required
              className={inputClass}
            />
          </FormField>
          <FormField label='Repeat password'>
            <input
              type='password'
              value={repeatPassword}
              onChange={(e) => setRepeatPassword(e.target.value)}
              placeholder='Repeat your password'
              autoComplete='new-password'
              required
              className={`border rounded-lg px-3 py-2.5 text-sm text-iq-black placeholder:text-iq-grey-medium focus:outline-none transition-colors ${
                repeatPassword && !passwordsMatch
                  ? 'border-iq-red focus:border-iq-red'
                  : 'border-iq-grey-light focus:border-iq-grey-medium'
              }`}
            />
            {repeatPassword && !passwordsMatch && (
              <p className='text-xs text-iq-red'>Passwords do not match.</p>
            )}
          </FormField>
          {error && <p className='text-xs text-red-500'>{error}</p>}
          <button
            type='submit'
            disabled={!isValid || submitting}
            className='flex items-center justify-center gap-2 px-6 py-3 bg-iq-red text-white text-sm font-semibold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-40 mt-2'>
            <ChevronRight className='w-4 h-4' />
            {submitting ? 'Creating account…' : 'Create account'}
          </button>
        </form>
      )}
    </div>
  );
}
